// fallow-ignore-file code-duplication
import { Link } from '@tanstack/react-router';
import { T } from 'gt-react';
import type { App } from '@erp/rpc';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useApps } from '@/api/rpc/hooks/apps/useApps';
import { HeaderPageTitle } from '@/context/header-title.context';
import { useAppsAgentModule } from './useAppsAgentModule';

/** The organization's Apps: pages agents built over Gently tools. */
export function AppsPage() {
  const { data: apps, isLoading, error } = useApps();
  useAppsAgentModule();

  return (
    <div className="mx-auto w-full max-w-4xl space-y-4 p-4">
      <div>
        <HeaderPageTitle>
          <T context="apps page title">Apps</T>
        </HeaderPageTitle>
        <p className="text-sm text-muted-foreground">
          <T context="apps page description">
            Pages built by agents over Gently tools. Each App runs as its
            background agent.
          </T>
        </p>
      </div>

      {error ? (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          {error instanceof Error ? error.message : String(error)}
        </div>
      ) : null}

      <div className="rounded-md border bg-card">
        {isLoading ? (
          <div className="space-y-2 p-3">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        ) : (
          <AppsTable apps={apps ?? []} />
        )}
      </div>
    </div>
  );
}

function AppsTable({ apps }: { apps: readonly App[] }) {
  if (apps.length === 0) {
    return (
      <p className="p-3 text-sm text-muted-foreground">
        <T context="apps page empty state">No Apps yet.</T>
      </p>
    );
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>
            <T context="apps table column header">Name</T>
          </TableHead>
          <TableHead>
            <T context="apps table column header">Description</T>
          </TableHead>
          <TableHead>
            <T context="apps table column header">Automatic fixes</T>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {apps.map((app) => (
          <TableRow key={app.id}>
            <TableCell className="font-medium">
              <Link
                to="/apps/$appId"
                params={{ appId: app.id }}
                className="hover:underline"
              >
                {app.name}
              </Link>
            </TableCell>
            <TableCell className="text-muted-foreground">
              {app.description ?? ''}
            </TableCell>
            <TableCell>
              {app.autoUpdateEnabled ? (
                <Badge variant="secondary">
                  <T context="apps table: automatic fixes on">On</T>
                </Badge>
              ) : (
                <Badge variant="outline">
                  <T context="apps table: automatic fixes off">Off</T>
                </Badge>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
